import { useEffect, useMemo, useRef, useState } from 'react'
import { CaretDown } from '@phosphor-icons/react'
import './PhoneInputField.scss'
import { DEFAULT_PHONE_COUNTRY_OPTIONS } from './countryPhoneCodes'
import { Box, ListItemText, MenuItem, Select } from '@mui/material'

const getFlagEmojiFromCode = (countryCode) => {
    const code = String(countryCode || '').toUpperCase().trim()
    if (!/^[A-Z]{2}$/.test(code)) return null

    const OFFSET = 127397
    return String.fromCodePoint(...code.split('').map((char) => char.charCodeAt(0) + OFFSET))
}

const getCountryDisplayFlag = (country) => {
    if (country?.flag) return country.flag
    return getFlagEmojiFromCode(country?.code) || country?.code || 'CC'
}

export default function PhoneInputField({
    value = '',
    onChange,
    countryCode = '+60',
    onCountryCodeChange,
    countryOptions = DEFAULT_PHONE_COUNTRY_OPTIONS,
    placeholder = 'Enter phone number',
    disabled = false,
    name,
    id,
    className = '',
    inputMode = 'numeric',
    allowOnlyDigits = true,
}) {
    const [open, setOpen] = useState(false)
    const [searchTerm, setSearchTerm] = useState('')
    const wrapperRef = useRef(null)
    const searchInputRef = useRef(null)

    const selectedCountry = useMemo(() => {
        return countryOptions.find((item) => item.value === countryCode) || countryOptions[0] || null
    }, [countryOptions, countryCode])

    const filteredCountryOptions = useMemo(() => {
        const keyword = searchTerm.trim().toLowerCase()
        if (!keyword) return countryOptions

        const keywordDigits = keyword.replace(/\D/g, '')

        return countryOptions.filter((option) => {
            const countryCodeValue = String(option.value || '').toLowerCase().replace(/\D/g, '')
            const countryCodeLabel = String(option.code || '').toLowerCase()
            const countryLabel = String(option.label || '').toLowerCase()

            if (keywordDigits) {
                return countryCodeValue.includes(keywordDigits)
            }

            return countryLabel.includes(keyword) || countryCodeLabel.includes(keyword)
        })
    }, [countryOptions, searchTerm])

    useEffect(() => {
        if (!open) {
            setSearchTerm('')
            return
        }

        if (searchInputRef.current) {
            searchInputRef.current.focus()
        }
    }, [open])

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!wrapperRef.current || wrapperRef.current.contains(event.target)) return
            setOpen(false)
        }

        document.addEventListener('mousedown', handleOutsideClick)
        return () => document.removeEventListener('mousedown', handleOutsideClick)
    }, [])

    const handleInputChange = (event) => {
        const nextValue = event.target.value
        const normalizedValue = allowOnlyDigits ? nextValue.replace(/\D/g, '') : nextValue

        if (onChange) {
            onChange(normalizedValue)
        }
    }

    const handleCountryChange = (nextCode) => {
        if (disabled) return

        if (onCountryCodeChange) {
            onCountryCodeChange(nextCode)
        }

        setOpen(false)
    }

    return (
        <div className={`phone-input-field ${disabled ? 'phone-input-field--disabled' : ''} ${className}`.trim()} ref={wrapperRef}>
            <Select
                value={countryCode}
                onChange={(e) => onCountryCodeChange?.(e.target.value)}
                disabled={disabled}
                variant="standard"
                disableUnderline
                className="phone-input-field__country-select"
                IconComponent={CaretDown}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            mt: 1,
                            borderRadius: 2,
                            maxHeight: 320,
                            width: 260,
                        },
                    },
                }}
                renderValue={(value) => {
                    const country =
                        countryOptions.find(item => item.value === value) ||
                        countryOptions[0];

                    return (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            <span>
                                {getCountryDisplayFlag(country)}
                            </span>

                            {/* <span>{country.value}</span> */}
                        </Box>
                    );
                }}

            >
                {countryOptions.map(option => (
                    <MenuItem
                        key={option.value}
                        value={option.value}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                width: '100%',
                            }}
                        >
                            <span>
                                {getCountryDisplayFlag(option)}
                            </span>

                            <ListItemText
                                primary={option.label}
                                secondary={option.value}
                            />
                        </Box>
                    </MenuItem>
                ))}
            </Select>
            <input
                type="tel"
                className="phone-input-field__input"
                value={value}
                onChange={handleInputChange}
                placeholder={placeholder}
            />
        </div>
    )
}
